"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import { getCountries, getCountryCallingCode, getExampleNumber, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------------------------
   Common mobile-number field: country flag + dial code picker (searchable) + a digits-only box that
   stops at the number of digits the chosen country uses.

   value / onChange use one string: "+91 9876543210" ("" when nothing is typed).
   Numbers saved earlier without a country code ("9876543210") are read as Indian numbers.
   --------------------------------------------------------------------------------------------- */

export interface Country {
  code: CountryCode;
  name: string;
  dial: string;
  /** Digits in a mobile number of this country (after the country code). */
  digits: number;
}

const DEFAULT_COUNTRY: CountryCode = "IN";
const PRIORITY: CountryCode[] = ["IN", "AE", "US", "GB", "CA", "AU", "SG", "SA", "QA", "KW", "OM", "BH", "NP", "BD", "LK"];
const FALLBACK_DIGITS = 10;
// Several countries share one dial code (+1, +7, +44 …); this one is picked when a number only gives the code.
const MAIN_COUNTRY: Record<string, CountryCode> = { "1": "US", "7": "RU", "44": "GB", "61": "AU", "212": "MA", "262": "RE", "290": "SH", "358": "FI", "590": "GP", "47": "NO", "39": "IT", "599": "CW" };

let cache: Country[] | null = null;
export function getCountryList(): Country[] {
  if (cache) return cache;
  const names = typeof Intl !== "undefined" && "DisplayNames" in Intl ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
  const all = getCountries().map<Country>((code) => ({
    code,
    name: names?.of(code) ?? code,
    dial: getCountryCallingCode(code),
    digits: getExampleNumber(code, examples)?.nationalNumber.length ?? FALLBACK_DIGITS,
  }));
  const rank = (c: Country) => {
    const i = PRIORITY.indexOf(c.code);
    return i === -1 ? PRIORITY.length : i;
  };
  cache = all.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  return cache;
}

const byCode = (code: CountryCode) => getCountryList().find((c) => c.code === code) as Country;

/** Splits a stored value into its country and the national digits. */
function parseValue(value: string, fallback: CountryCode): { country: Country; digits: string } {
  const raw = (value ?? "").trim();
  if (raw.startsWith("+")) {
    const parsed = parsePhoneNumberFromString(raw);
    const list = getCountryList();
    const dial = parsed?.countryCallingCode ?? list.find((c) => raw.slice(1).startsWith(c.dial))?.dial;
    if (dial) {
      const country = (parsed?.country && byCode(parsed.country)) || list.find((c) => c.code === MAIN_COUNTRY[dial] && c.dial === dial) || list.find((c) => c.dial === dial) || byCode(fallback);
      const rest = raw.slice(1).replace(/\D/g, "").slice(String(dial).length);
      return { country, digits: rest };
    }
  }
  // legacy / local number: keep the digits (drop a leading 0 or the old 91 prefix)
  let digits = raw.replace(/\D/g, "");
  const fb = byCode(fallback);
  if (digits.length > fb.digits && digits.startsWith(fb.dial)) digits = digits.slice(fb.dial.length);
  if (digits.length > fb.digits && digits.startsWith("0")) digits = digits.replace(/^0+/, "");
  return { country: fb, digits };
}

export const formatPhone = (country: Country, digits: string) => (digits ? `+${country.dial} ${digits}` : "");

/** "" when the number is fine (or empty and optional); otherwise a message for the field. */
export function validatePhone(value: string, { required = true }: { required?: boolean } = {}): string {
  const { country, digits } = parseValue(value, DEFAULT_COUNTRY);
  if (!digits) return required ? "Mobile number is required" : "";
  if (digits.length !== country.digits) return `Enter a ${country.digits}-digit ${country.name} mobile number`;
  return "";
}

function Flag({ code, className }: { code: CountryCode; className?: string }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <span className={cn("inline-flex h-[15px] w-5 items-center justify-center rounded-sm bg-line text-[9px] font-bold text-ink-muted", className)}>{code}</span>;
  return (
    // eslint-disable-next-line @next/next/no-img-element -- tiny external flag image
    <img
      src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
      width={20}
      height={15}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={cn("h-[15px] w-5 rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]", className)}
    />
  );
}

export function PhoneInput({
  value,
  onChange,
  id,
  name,
  error,
  disabled,
  placeholder,
  className,
  defaultCountry = DEFAULT_COUNTRY,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  defaultCountry?: CountryCode;
  "aria-label"?: string;
}) {
  const autoId = useId();
  const inputId = id ?? `phone-${autoId}`;
  const parsed = useMemo(() => parseValue(value, defaultCountry), [value, defaultCountry]);
  // the picked country is remembered even while the number box is empty
  const [picked, setPicked] = useState<Country | null>(null);
  const country = value.trim() ? parsed.country : (picked ?? parsed.country);
  const digits = parsed.digits;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  function openList() {
    const r = box.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ left: r.left, top: r.bottom + 6, width: Math.max(r.width, 300) });
    setQuery("");
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const close = (e: Event) => {
      if (popup.current?.contains(e.target as Node) || trigger.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    const onResize = () => setOpen(false);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", close, true);
    window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, "");
    const all = getCountryList();
    return q ? all.filter((c) => c.name.toLowerCase().includes(q) || c.dial.startsWith(q) || c.code.toLowerCase() === q) : all;
  }, [query]);

  function pickCountry(c: Country) {
    setPicked(c);
    setOpen(false);
    onChange(formatPhone(c, digits.slice(0, c.digits)));
    document.getElementById(inputId)?.focus();
  }

  function onType(raw: string) {
    // pasting a full international number switches the country to match it
    if (raw.trim().startsWith("+")) {
      const p = parseValue(raw, defaultCountry);
      setPicked(p.country);
      onChange(formatPhone(p.country, p.digits.slice(0, p.country.digits)));
      return;
    }
    const d = raw.replace(/\D/g, "").slice(0, country.digits);
    onChange(formatPhone(country, d));
  }

  return (
    <div
      ref={box}
      className={cn(
        "flex h-11 w-full items-stretch rounded-xl border bg-white text-sm text-ink transition focus-within:ring-2",
        error ? "border-brand-500 focus-within:border-brand-500 focus-within:ring-brand-600/20" : "border-line focus-within:border-brand-500 focus-within:ring-brand-600/15",
        disabled && "opacity-60",
        className,
      )}
    >
      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country code: ${country.name} +${country.dial}`}
        className="flex shrink-0 items-center gap-1.5 rounded-l-xl border-r border-line px-3 font-medium hover:bg-cream/60"
      >
        <Flag code={country.code} />
        <span className="tabular-nums">+{country.dial}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 text-ink-muted transition-transform", open && "rotate-180")} />
      </button>
      <input
        id={inputId}
        name={name}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={!!error}
        value={digits}
        onChange={(e) => onType(e.target.value)}
        maxLength={country.digits + 20} /* room to paste "+91 98765 43210"; typing itself is clamped in onType */
        placeholder={placeholder}
        className="min-w-0 flex-1 rounded-r-xl bg-transparent px-3 outline-none placeholder:text-ink-muted/70"
      />

      {open &&
        pos &&
        createPortal(
          <div
            ref={popup}
            role="listbox"
            style={{ position: "fixed", left: Math.min(pos.left, window.innerWidth - pos.width - 8), top: pos.top, width: Math.min(pos.width, window.innerWidth - 16), zIndex: 1000 }}
            className="overflow-hidden rounded-xl border border-line bg-white shadow-lift"
          >
            <div className="relative border-b border-line p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code…"
                aria-label="Search country or code"
                className="h-9 w-full rounded-lg border border-line bg-cream/50 pl-9 pr-3 text-sm outline-none focus:border-brand-500"
              />
            </div>
            <ul className="max-h-64 overflow-y-auto py-1">
              {list.length === 0 && <li className="px-4 py-3 text-sm text-ink-muted">No country found</li>}
              {list.map((c) => (
                <li key={c.code} role="option" aria-selected={c.code === country.code}>
                  <button
                    type="button"
                    onClick={() => pickCountry(c)}
                    className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-cream", c.code === country.code && "bg-brand-600/5")}
                  >
                    <Flag code={c.code} />
                    <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                    <span className="tabular-nums text-ink-soft">+{c.dial}</span>
                    {c.code === country.code && <Check className="h-4 w-4 text-brand-600" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
